'use client';

import { useState } from 'react';
import Link from 'next/link';
import Board from './components/Board';
import ScorePanel from './components/ScorePanel';
import GameControls from './components/GameControls';
import GameOverModal from './components/GameOverModal';
import { use2048Game } from './hooks/use2048Game';

export default function Game2048() {
  const {
    gameState,
    handleTouch,
    restartGame,
    continueGame,
    WINNING_TILE
  } = use2048Game();
  
  const [showModal, setShowModal] = useState(true);
  
  // Close modal without restarting game
  const handleCloseModal = () => {
    setShowModal(false);
  };
  
  // Handle game over or winning condition
  const shouldShowModal = showModal && (gameState.isGameOver || (gameState.hasWon && !gameState.canContinue));

  return (
    <div className="min-h-screen bg-[#faf8ef] text-gray-800">
      <div className="container mx-auto px-4 py-8 text-center max-w-md">
        <header className="mb-4">
          <div className="flex justify-between items-center mb-6">
            <Link href="/" className="btn btn-sm bg-[#8f7a66] hover:bg-[#7c6957] text-white border-none">
              &larr; Back Home
            </Link>
            
            <h1 className="text-4xl font-bold text-[#776e65]">2048</h1>
            
            <div className="w-16"></div> {/* Empty div for layout balance */}
          </div>
          
          <ScorePanel score={gameState.score} highScore={gameState.highScore} />
        </header>
        
        <p className="text-gray-600 text-sm mb-4">
          Swipe or use arrow keys to move tiles
        </p>
        
        <main>
          <div className="flex justify-center items-center mb-6">
            <Board 
              board={gameState.board} 
              onMove={handleTouch}
            />
          </div>
          
          <GameControls onRestart={restartGame} />
        </main>
        
        <GameOverModal
          isVisible={shouldShowModal}
          score={gameState.score}
          hasWon={gameState.hasWon && !gameState.canContinue}
          onRestart={restartGame}
          onContinue={continueGame}
          onClose={handleCloseModal}
        />
        
        <footer className="mt-10 text-center text-[#776e65] font-medium">
          <p>© 2025 Vibe Games - Your perfect work break companion</p>
        </footer>
        
        <style jsx global>{`
          .score-box {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
          }
          
          .score-label {
            @apply text-xs font-semibold text-[#eee4da];
          }
          
          .score-value {
            @apply text-xl font-bold text-white;
          }
          
          @keyframes tile-appear {
            0% { opacity: 0; transform: scale(0.5); }
            100% { opacity: 1; transform: scale(1); }
          }
          
          @keyframes tile-merge {
            0% { transform: scale(1); }
            50% { transform: scale(1.2); }
            100% { transform: scale(1); }
          }
          
          .animate-tile-appear {
            animation: tile-appear 0.2s ease-in-out;
          }
          
          .animate-tile-merge {
            animation: tile-merge 0.2s ease-in-out;
          }
        `}</style>
      </div>
    </div>
  );
}